import { useEffect, useRef } from 'react';

import { useCohortDefinitionStore } from '@verantos-dev/app-state-cohort-definition';
import { useCohortDefinitionQuery } from '@verantos-dev/feature-analytical-variables';
import type { CohortVersionParams } from '@verantos-dev/feature-analytical-variables';

// TODO: cleanup with explicit_save ff — delete this file when the flag rolls out

/**
 * Legacy auto-save TanStack Query → Zustand sync.
 *
 * Re-hydrates the store whenever TQ hands back a new data reference, as long
 * as there are no local staged changes. Auto-save relies on this
 * reconciliation: after a BE write, TQ refetches and writes the canonical
 * server state back into the store.
 */
export const useCohortDefinitionDataLegacy = (
  params: CohortVersionParams & { enabled?: boolean }
) => {
  const { enabled, ...queryParams } = params;
  const { data, isLoading, error } = useCohortDefinitionQuery(queryParams, { enabled });
  const setFromServer = useCohortDefinitionStore((s) => s.setFromServer);
  const hasStagedChanges = useCohortDefinitionStore((s) => s.hasStagedChanges);
  const lastProcessedDataRef = useRef<typeof data | undefined>();

  useEffect(() => {
    if (!enabled || !data || hasStagedChanges) return;
    if (data === lastProcessedDataRef.current) return;
    lastProcessedDataRef.current = data;

    setFromServer(data.analyticalVariables, data.criteria, data.studyPeriod, {
      simpleEvents: data.simpleEvents,
      derivedEvents: data.derivedEvents,
      indexDate: data.indexDate,
      subjectVariables: data.subjectVariables,
    });
  }, [enabled, data, hasStagedChanges, setFromServer]);

  return { isLoading, error };
};
